import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { RouterService } from 'app/core/navigation';
import { PARENTS_ROUTE } from 'app/parents/parents-routing.constant';
import { STUDENT_ROUTE } from 'app/routing-constants/student-routing.constant';
import { StudentService } from 'app/student/services/student.service';
import { TEACHERS_ROUTE } from 'app/teachers/teachers-routing.constant';
import { APP_ROUTE } from '../../../app-routing.constant';
import { AuthenticationService, PermissionsService, UserRole } from '../../authentication';
import { DEFAULT_PAGE_ROLE_MAPPING, NAVIGATION_URLS, PageRoleMapping } from '../constants';

@Injectable({
  providedIn: 'root'
})
export class DefaultPageRouteService {
  private previousRedirectKey = 'kh_previous_redirect';

  public constructor(
    private readonly authService: AuthenticationService,
    private readonly permissionsService: PermissionsService,
    private readonly router: Router,
    private readonly routerService: RouterService,
    private readonly studentService: StudentService) { }

  public async routeToDefaultPage(): Promise<void> {
    const roles = this.authService.getUserClaims().roles;

    if (roles.includes(UserRole.viewOnlyParent)) {
      this.authService.logOut();
      window.location.href = NAVIGATION_URLS.VIEW_ONLY_PARENT_URL;
    }

    let highestPriorityRole: PageRoleMapping | undefined;

    for (const role of roles) {
      if (!Object.values(UserRole).includes(role as UserRole)) {
        continue;
      }

      const roleMapping = DEFAULT_PAGE_ROLE_MAPPING[role as UserRole];

      if (!highestPriorityRole || roleMapping.priority < highestPriorityRole.priority) {
        highestPriorityRole = roleMapping;
      }
    }

    if (highestPriorityRole) {
      await this.router.navigate(highestPriorityRole.state);
    }
  }

  public async routeToDefaultSelectedRole(role: string): Promise<void> {
    if (role && Object.values(UserRole).includes(role as UserRole)) {
      const roleMapping = DEFAULT_PAGE_ROLE_MAPPING[role as UserRole];

      await this.router.navigate(roleMapping.state);
    }
  }

  public getCurrentFallbackPage(): string {
    if (this.permissionsService.isInRole(UserRole.student)) {
      return NAVIGATION_URLS.STUDENT_FALLBACK_PAGE;
    }

    return NAVIGATION_URLS.PUBLIC_FALLBACK_PAGE;
  }

  public async navigateToPublicFallback(): Promise<void> {
    const interruptedUrl = this.authService.getInterruptedUrl();
    const previousRedirect = localStorage.getItem(this.previousRedirectKey);
    const fallbackUrl = this.getCurrentFallbackPage();

    if (interruptedUrl.split('?')[0].toLowerCase() === fallbackUrl.toLowerCase()) {
      this.authService.setInterruptedUrl('');
      location.href = location.href.split('?')[0];
      localStorage.removeItem(this.previousRedirectKey);
      await this.router.navigateByUrl(fallbackUrl, { replaceUrl: true });

      return;
    }

    if (interruptedUrl && this.routerService.IsExternalGuarded(fallbackUrl)) {
      await this.router.navigate(APP_ROUTE.RFA.route(),
        { queryParams: APP_ROUTE.RFA.queryParams(fallbackUrl, interruptedUrl), replaceUrl: true });

      return;
    }

    if (interruptedUrl && previousRedirect !== interruptedUrl) {
      localStorage.setItem(this.previousRedirectKey, interruptedUrl);
      await this.router.navigateByUrl(`${fallbackUrl}?RedirectFromUrl=${interruptedUrl}`, { replaceUrl: true });
    } else {
      localStorage.setItem(this.previousRedirectKey, '');
      await this.router.navigateByUrl(fallbackUrl, { replaceUrl: true });
    }
  }

  public async navigateToForbidden(): Promise<void> {
    localStorage.removeItem(this.previousRedirectKey);
    await this.router.navigateByUrl(NAVIGATION_URLS.FORBIDDEN_FALLBACK_PAGE);
  }

  public async navigateToSettings(): Promise<void> {
    const roles = this.authService.getUserClaims().roles;
    const queryParams = { show: 'EmailNotification' };
    if (roles.includes(UserRole.parent)) {
      await this.routerService.navigate(PARENTS_ROUTE.SETTINGS.route(), { queryParams });

      return;
    }

    if (roles.includes(UserRole.teacher)) {
      await this.routerService.navigate(TEACHERS_ROUTE.SETTINGS.route(), { queryParams });

      return;
    }

    if (roles.includes(UserRole.student)) {
      await this.routerService.navigate(STUDENT_ROUTE.SETTINGS.route(this.studentService.activeClassId), { queryParams });

      return;
    }

    await this.routeToDefaultPage();
  }
}
